import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import api, { errMsg } from "@/lib/api";

export default function CvTools() {
  const [profile, setProfile] = useState(null);
  const [cv, setCv] = useState({ headline: "", skills: "", experience_years: 0, summary: "", target_role: "" });
  const [cvResult, setCvResult] = useState(null);
  const [prep, setPrep] = useState({ role: "", company: "", experience: 0 });
  const [prepResult, setPrepResult] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [versions, setVersions] = useState([]);
  const [parsedResume, setParsedResume] = useState(null);
  const [builder, setBuilder] = useState({ name: "", template: "professional", headline: "", summary: "", skills: "", experience_text: "", education_text: "", certifications: "" });
  const [busy, setBusy] = useState("");

  const loadVersions = () => {
    api.get("/candidate/cvs").then(({ data }) => setVersions(data.cvs)).catch(() => {});
  };

  useEffect(() => {
    api.get("/candidate/overview").then(({ data }) => {
      const p = data.profile || {};
      setProfile(p);
      setCv({
        headline: p.headline || "", skills: (p.skills || []).join(", "),
        experience_years: p.experience_years || 0, summary: p.summary || "", target_role: "",
      });
      setBuilder((b) => ({
        ...b, headline: p.headline || "", summary: p.summary || "",
        skills: (p.skills || []).join(", "), name: `${p.headline || "My"} CV`,
      }));
    }).catch(() => {});
    api.get("/candidate/cv-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {});
    loadVersions();
  }, []);

  const saveCv = async () => {
    setBusy("save");
    try {
      await api.post("/candidate/cvs", {
        ...builder, skills: builder.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("CV version saved");
      loadVersions();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const downloadCv = async (v) => {
    try {
      const res = await api.get(`/candidate/cvs/${v.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${v.name.replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const scoreCv = async () => {
    setBusy("cv");
    setCvResult(null);
    try {
      const { data } = await api.post("/ai/cv-score", {
        ...cv, skills: cv.skills.split(",").map((s) => s.trim()).filter(Boolean),
        experience_years: Number(cv.experience_years),
      });
      setCvResult(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const genPrep = async () => {
    setBusy("prep");
    setPrepResult(null);
    try {
      const { data } = await api.post("/ai/interview-prep", { ...prep, experience: Number(prep.experience) });
      setPrepResult(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const List = ({ items, color = "text-[#2563EB]" }) => (
    <ul className="space-y-2">
      {(items || []).map((x, i) => (
        <li key={i} className="text-sm flex items-start gap-2">
          <span className={`${color} font-bold mt-0.5`}>•</span> <span className="text-muted-foreground">{x}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <div data-testid="cv-tools-page">
      <Tabs defaultValue="builder">
        <TabsList className="mb-6">
          <TabsTrigger value="builder" data-testid="tab-cv-builder">CV Builder</TabsTrigger>
          <TabsTrigger value="cv" data-testid="tab-cv-score">AI CV Score</TabsTrigger>
          <TabsTrigger value="prep" data-testid="tab-interview-prep">AI Interview Prep</TabsTrigger>
        </TabsList>

        <TabsContent value="builder">
          <div className="bg-white border border-[#2563EB]/30 rounded-xl p-5 mb-6 flex flex-wrap items-center justify-between gap-4" data-testid="resume-import-card">
            <div>
              <p className="font-heading font-semibold text-[#0B1B3D] text-sm">Import from resume</p>
              <p className="text-xs text-muted-foreground mt-1">Upload a PDF/DOCX/TXT resume — AI extracts your profile fields for review.</p>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="file"
                id="resume-upload"
                accept=".pdf,.docx,.txt"
                className="hidden"
                data-testid="resume-file-input"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  setBusy("resume");
                  try {
                    const fd = new FormData();
                    fd.append("file", f);
                    const { data } = await api.post("/candidate/resume/parse", fd, {
                      headers: { "Content-Type": "multipart/form-data" },
                    });
                    const p = data.parsed;
                    setBuilder((b) => ({
                      ...b,
                      headline: p.headline || b.headline,
                      summary: p.summary || b.summary,
                      skills: (p.skills || []).join(", ") || b.skills,
                      education_text: p.qualification || b.education_text,
                    }));
                    setParsedResume(p);
                    toast.success("Resume parsed — review the fields below");
                  } catch (err) {
                    toast.error(errMsg(err));
                  } finally {
                    setBusy("");
                    e.target.value = "";
                  }
                }}
              />
              <Button variant="outline" className="rounded-full" disabled={busy === "resume"} onClick={() => document.getElementById("resume-upload").click()} data-testid="resume-upload-btn">
                {busy === "resume" ? "Parsing with AI…" : "Upload Resume"}
              </Button>
            </div>
          </div>
          {parsedResume && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6" data-testid="resume-parsed-card">
              <p className="text-sm font-semibold text-emerald-900 mb-2">AI extracted: {parsedResume.headline} · {parsedResume.experience_years} yrs · {parsedResume.location}</p>
              <p className="text-xs text-emerald-800 mb-3">Skills: {(parsedResume.skills || []).join(", ")}</p>
              <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={async () => {
                try {
                  await api.put("/candidate/profile", parsedResume);
                  toast.success("Profile updated from resume");
                  setParsedResume(null);
                } catch (e) { toast.error(errMsg(e)); }
              }} data-testid="resume-apply-btn">
                Apply to My Profile
              </Button>
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border/60 rounded-xl p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Version name</Label><Input value={builder.name} onChange={(e) => setBuilder({ ...builder, name: e.target.value })} className="mt-1.5" data-testid="builder-name" /></div>
                <div><Label>Headline</Label><Input value={builder.headline} onChange={(e) => setBuilder({ ...builder, headline: e.target.value })} className="mt-1.5" data-testid="builder-headline" /></div>
              </div>
              <div>
                <Label>Template</Label>
                <div className="flex gap-2 flex-wrap mt-2">
                  {templates.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setBuilder({ ...builder, template: t.id })}
                      data-testid={`template-${t.id}`}
                      className={`text-xs font-semibold rounded-full px-4 py-2 border transition-colors ${
                        builder.template === t.id ? "bg-[#0B1B3D] text-white border-[#0B1B3D]" : "bg-white border-border hover:border-[#2563EB]"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div><Label>Professional summary</Label><Textarea rows={3} value={builder.summary} onChange={(e) => setBuilder({ ...builder, summary: e.target.value })} className="mt-1.5" data-testid="builder-summary" /></div>
              <div><Label>Skills (comma separated)</Label><Input value={builder.skills} onChange={(e) => setBuilder({ ...builder, skills: e.target.value })} className="mt-1.5" data-testid="builder-skills" /></div>
              <div><Label>Experience (one per line)</Label><Textarea rows={4} value={builder.experience_text} onChange={(e) => setBuilder({ ...builder, experience_text: e.target.value })} placeholder={"HR Executive — ABC Corp\n2022 – Present · Payroll & recruitment"} className="mt-1.5" data-testid="builder-experience" /></div>
              <div><Label>Education (one per line)</Label><Textarea rows={2} value={builder.education_text} onChange={(e) => setBuilder({ ...builder, education_text: e.target.value })} className="mt-1.5" data-testid="builder-education" /></div>
              <div><Label>Certifications</Label><Input value={builder.certifications} onChange={(e) => setBuilder({ ...builder, certifications: e.target.value })} className="mt-1.5" /></div>
              <Button onClick={saveCv} disabled={busy === "save" || !builder.name} className="rounded-full bg-[#0B1B3D] w-full" data-testid="save-cv-btn">
                {busy === "save" ? "Saving…" : "Save CV Version"}
              </Button>
            </div>
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="cv-versions">
              <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">My CV versions</h3>
              {versions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-10 text-center">Save a version to download a branded PDF.</p>
              ) : (
                <div className="space-y-3">
                  {versions.map((v) => (
                    <div key={v.id} className="border border-border/60 rounded-lg p-4 flex items-center justify-between" data-testid={`cv-version-${v.id}`}>
                      <div>
                        <p className="font-semibold text-sm">{v.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{v.template} template</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadCv(v)} data-testid={`cv-pdf-${v.id}`}>PDF</Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => { await api.delete(`/candidate/cvs/${v.id}`); loadVersions(); }} data-testid={`cv-del-${v.id}`}>Delete</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="cv">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border/60 rounded-xl p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                Your profile is pre-filled. Edit and let AI score your CV with keyword and skill recommendations.
              </p>
              <div><Label>Headline</Label><Input value={cv.headline} onChange={(e) => setCv({ ...cv, headline: e.target.value })} className="mt-1.5" data-testid="cv-headline" /></div>
              <div><Label>Skills</Label><Input value={cv.skills} onChange={(e) => setCv({ ...cv, skills: e.target.value })} className="mt-1.5" data-testid="cv-skills" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Experience (yrs)</Label><Input type="number" value={cv.experience_years} onChange={(e) => setCv({ ...cv, experience_years: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Target role</Label><Input value={cv.target_role} onChange={(e) => setCv({ ...cv, target_role: e.target.value })} className="mt-1.5" data-testid="cv-target" /></div>
              </div>
              <div><Label>Professional summary</Label><Textarea rows={4} value={cv.summary} onChange={(e) => setCv({ ...cv, summary: e.target.value })} className="mt-1.5" data-testid="cv-summary" /></div>
              <Button onClick={scoreCv} disabled={busy === "cv"} className="rounded-full bg-[#0B1B3D] w-full" data-testid="cv-score-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> {busy === "cv" ? "Scoring…" : "Score My CV"}
              </Button>
            </div>
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="cv-result">
              {cvResult ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <p className="font-heading font-extrabold text-5xl text-[#2563EB]" data-testid="cv-score-value">{cvResult.score}</p>
                    <p className="text-xs text-muted-foreground mt-1">AI CV Score / 100</p>
                  </div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2 text-emerald-700">Strengths</h4><List items={cvResult.strengths} color="text-emerald-500" /></div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2 text-amber-700">Missing skills</h4><List items={cvResult.missing_skills} color="text-amber-500" /></div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2">Keyword recommendations</h4><List items={cvResult.keyword_recommendations} /></div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2">Formatting</h4><List items={cvResult.formatting_recommendations} /></div>
                </div>
              ) : (
                <div className="h-full grid place-items-center text-sm text-muted-foreground py-20">
                  Run the AI scorer to see strengths, gaps and recommendations.
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="prep">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-border/60 rounded-xl p-6 space-y-4">
              <div><Label>Role</Label><Input value={prep.role} onChange={(e) => setPrep({ ...prep, role: e.target.value })} placeholder="e.g. B2B Sales Manager" className="mt-1.5" data-testid="prep-role" /></div>
              <div><Label>Company</Label><Input value={prep.company} onChange={(e) => setPrep({ ...prep, company: e.target.value })} className="mt-1.5" data-testid="prep-company" /></div>
              <div><Label>Your experience (yrs)</Label><Input type="number" value={prep.experience} onChange={(e) => setPrep({ ...prep, experience: e.target.value })} className="mt-1.5" data-testid="prep-exp" /></div>
              <Button onClick={genPrep} disabled={busy === "prep" || !prep.role} className="rounded-full bg-[#0B1B3D] w-full" data-testid="prep-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> {busy === "prep" ? "Preparing…" : "Generate Interview Prep"}
              </Button>
            </div>
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="prep-result">
              {prepResult ? (
                <div className="space-y-6">
                  <div><h4 className="font-heading font-semibold text-sm mb-2">Technical questions</h4><List items={prepResult.technical_questions} /></div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2">HR questions</h4><List items={prepResult.hr_questions} /></div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2">Behavioral questions</h4><List items={prepResult.behavioral_questions} /></div>
                  <div><h4 className="font-heading font-semibold text-sm mb-2 text-[#F5A623]">Tips</h4><List items={prepResult.tips} color="text-[#F5A623]" /></div>
                </div>
              ) : (
                <div className="h-full grid place-items-center text-sm text-muted-foreground py-20">
                  AI will generate likely technical, HR and behavioral questions.
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
